/**
 * Pouvoir d'achat et prix du panier (PRD §14.2, ADR 0005). Fonctions pures, tous les coefficients dans `params` :
 * - transmission des prélèvements aux prix : TVA (`pt_tva_passthrough`, immédiate), TICPE (`pt_ticpe_passthrough`),
 *   impôts sur les entreprises — levier IS et mesures `fiscalite_entreprises` — (`pt_firm_tax_passthrough`, montée sur
 *   `pt_firm_tax_passthrough_years` ans) ; effet moyen sur les prix à la consommation = hausse brute / consommation des
 *   ménages (`pp_household_consumption_bn`) × taux de transmission ;
 * - prix d'un poste du panier = effet moyen du canal × sensibilité du poste (`price_<poste>_sens_<canal>`) ;
 * - revenu disponible : IR et niches (`pp_niche_household_share`) en moins ; coût net des mesures × part qui atteint les
 *   ménages selon la catégorie (`pp_transfer_share_<catégorie>`) ; `fx.pp` explicite.
 * Une mesure qui porte `fx.prices` est prise telle quelle (prix explicites + `fx.pp`), sans canal dérivé de sa catégorie.
 * Les prix sont des écarts au tendanciel en %, niveau ; ils n'entrent pas dans l'inflation macro (simplification v0.2).
 */
import { param } from "./params.js";
import { hypTarget, profileAt } from "./profile.js";
import { applyVariant, PCT } from "./state.js";
import { BASKET_ITEMS } from "./types.js";
import type { BasketItem, Financing, Measure, ParamSet, Programme } from "./types.js";

export type PriceChannel = "tva" | "ticpe" | "firm";
const CHANNELS: readonly PriceChannel[] = ["tva", "ticpe", "firm"];

export type Basket = Record<BasketItem, number>;
const zeroBasket = (): Basket => ({ bread: 0, fuel: 0, rent: 0, energy: 0 });

/** Effet d'une source (levier ou mesure) sur les prix et le revenu. */
export interface PolicyEffect {
  /** `lever:<id>` ou `measure:<id>`. */
  source: string;
  /** Δ % de prix par poste (écart au tendanciel). */
  prices: Basket;
  /** Δ % de l'indice des prix à la consommation (moyenne pondérée de la transmission). */
  cpi: number;
  /** Δ % du revenu disponible des ménages (nominal, hors prix). */
  income: number;
}

export interface PolicyEffects {
  prices: Basket;
  cpi: number;
  income: number;
  by_source: PolicyEffect[];
}

/** Coût net d'une mesure l'année k depuis l'adoption (curseur `cost_leak` appliqué). */
export function measureCostAt(m: Measure, k: number, hyp: number | undefined): number {
  let c = profileAt(m.cost, k, m.ramp);
  if (hypTarget(m) === "cost_leak" && hyp !== undefined) c *= 1 - hyp / PCT;
  return c;
}

function channelEffect(params: ParamSet, source: string, ch: PriceChannel, raiseBn: number, k: number): PolicyEffect {
  const P = (id: string): number => param(params, id);
  let pass = P(`pt_${ch === "firm" ? "firm_tax" : ch}_passthrough`);
  if (ch === "firm") pass *= Math.min(1, (k + 1) / Math.max(1, P("pt_firm_tax_passthrough_years")));
  const cpi = ((raiseBn / P("pp_household_consumption_bn")) * PCT) * pass;
  const prices = zeroBasket();
  for (const i of BASKET_ITEMS) prices[i] = cpi * P(`price_${i}_sens_${ch}`);
  return { source, prices, cpi, income: 0 };
}

/** Effet d'un levier de financement (Md€ bruts) l'année k (0 = première année du mandat). */
export function leverPolicyEffect(params: ParamSet, id: string, raiseBn: number, k: number): PolicyEffect {
  const src = `lever:${id}`;
  const Y = param(params, "pp_household_disposable_income_bn");
  if (id === "tva" || id === "ticpe") return channelEffect(params, src, id, raiseBn, k);
  if (id === "is") return channelEffect(params, src, "firm", raiseBn, k);
  const out: PolicyEffect = { source: src, prices: zeroBasket(), cpi: 0, income: 0 };
  if (id === "ir") out.income = (-raiseBn / Y) * PCT;
  else if (id === "niche") out.income = ((-raiseBn * param(params, "pp_niche_household_share")) / Y) * PCT;
  return out;
}

/** Effet d'une mesure l'année k depuis son adoption. */
export function measurePolicyEffect(params: ParamSet, m: Measure, k: number, hyp: number | undefined): PolicyEffect {
  const src = `measure:${m.id}`;
  const out: PolicyEffect = { source: src, prices: zeroBasket(), cpi: 0, income: 0 };
  if (k < 0) return out;
  if (m.fx.pp !== undefined) out.income += profileAt(m.fx.pp, k, m.ramp);
  if (m.fx.prices !== undefined) {
    for (const i of BASKET_ITEMS) {
      const p = m.fx.prices[i];
      if (p === undefined) continue;
      out.prices[i] = profileAt(p, k, m.ramp);
      out.cpi += out.prices[i] * param(params, `price_${i}_weight`);
    }
    return out;
  }
  const c = measureCostAt(m, k, hyp);
  if (m.category === "fiscalite_entreprises") {
    // coût > 0 = baisse d'impôt (prix en baisse), < 0 = hausse (prix en hausse), transmission progressive depuis l'adoption.
    const e = channelEffect(params, src, "firm", -c, k);
    return { ...e, income: out.income };
  }
  out.income += ((c * param(params, `pp_transfer_share_${m.category}`)) / param(params, "pp_household_disposable_income_bn")) * PCT;
  return out;
}

function sum(effects: PolicyEffect[]): PolicyEffects {
  const prices = zeroBasket();
  let cpi = 0;
  let income = 0;
  for (const e of effects) {
    for (const i of BASKET_ITEMS) prices[i] += e.prices[i];
    cpi += e.cpi;
    income += e.income;
  }
  return { prices, cpi, income, by_source: effects };
}

/**
 * Effets prix/revenu de l'ensemble des mesures (chacune à son ancienneté `k`) et des leviers (année `turn`, 0-based).
 * Utilisé par la simulation année par année.
 */
export function policyEffects(
  params: ParamSet,
  measures: { m: Measure; k: number; hyp: number | undefined }[],
  financing: Financing,
  turn: number,
): PolicyEffects {
  const effects: PolicyEffect[] = [];
  for (const [id, v] of Object.entries(financing.revenue_raises)) if (v !== 0) effects.push(leverPolicyEffect(params, id, v, turn));
  for (const x of measures) effects.push(measurePolicyEffect(params, x.m, x.k, x.hyp));
  return sum(effects);
}

export interface PriceEffectsReport {
  /** Première année du mandat (N+1). */
  n1: PolicyEffects;
  /** Cinquième année (N+5, régime). */
  n5: PolicyEffects;
}

/**
 * « Effet sur le prix du pain / du plein / du loyer / de l'énergie » d'un programme et de son financement, hors chocs
 * macroéconomiques : Δ % par poste à N+1 et N+5, total et détail par source (`by_source`, pour la fiche de chaque mesure
 * ou levier). Pur, sans simulation.
 */
export function priceEffects(params: ParamSet, programme: Programme, financing: Financing, catalogue: Measure[]): PriceEffectsReport {
  const resolved = programme.measure_ids.map((id) => {
    const m = catalogue.find((x) => x.id === id);
    if (!m) throw new Error(`priceEffects : mesure inconnue « ${id} »`);
    const v = programme.variants?.[id];
    return v !== undefined ? applyVariant(m, v) : m;
  });
  const at = (k: number): PolicyEffects =>
    policyEffects(
      params,
      resolved.map((m) => ({ m, k, hyp: programme.hyp[m.id] ?? m.hyp?.default })),
      financing,
      k,
    );
  return { n1: at(0), n5: at(param(params, "mandate_years") - 1) };
}

export { CHANNELS as PRICE_CHANNELS };
